import React, { Dispatch, SetStateAction } from 'react'
import { useIntl } from 'react-intl'
import { SnackbarProps } from '@material-ui/core/Snackbar'
import messages from '@utils/messages'

export enum SnackbarType {
  Success = 'snackbarSuccess',
  Error = 'snackbarError',
  SuccessAlert = 'snackbarSuccessAlert',
  InfoAlert = 'snackbarInfoAlert',
  WarningAlert = 'snackbarWarningAlert',
  ErrorAlert = 'snackbarErrorAlert',
}

type MessageAndType = [string, SnackbarType]

export interface GlobalSnackbarState {
  open: SnackbarProps['open']
  message?: SnackbarProps['message']
  variant?: SnackbarType | null
  disableWindowBlurListener?: boolean
  newToastFlag?: boolean
}

export enum SnackbarMessages {
  BackendError = 'backendError',
  GenericError = 'genericError',
  ItemsAddedToCart = 'itemsAddedToCart',
  resendVerificationLink = 'resendVerificationLink',
  resendEmailLink = 'resendEmailLink',
  CancelCartError = 'cancelCartError',
  CartContactInfoSaveError = 'cartContactInfoSaveError',
  CartContactPartyInfoSaveError = 'cartContactPartyInfoSaveError',
  CartCsrInstructionsSaved = 'cartCsrInstructionsSaved',
  CartCsrInstructionsError = 'cartCsrInstructionsError',
  CartErrorRequestingQuote = 'cartErrorRequestingQuote',
  CartErrorSubmitting = 'cartErrorSubmitting',
  CartErrorUpdating = 'cartErrorUpdating',
  CartSaveBillingAddressSuccess = 'cartSaveBillingAddressSuccess',
  CartSaveBillingAddressError = 'cartSaveBillingAddressError',
  CartSavePaymentInfoSuccess = 'cartSavePaymentInfoSuccess',
  CartSavePaymentInfoError = 'cartSavePaymentInfoError',
  CartShippingAddressSaveError = 'cartShippingAddressSaveError',
  CartShippingUpdateSuccess = 'cartShippingUpdateSuccess',
  CartShippingUpdateError = 'cartShippingUpdateError',
  CustomerNotAllowedToLinkProfileBefore = 'customerNotAllowedToLinkProfileBefore',
  ErrorNoRecordFoundForGivenCustomerDetails = 'errorNoRecordFoundForGivenCustomerDetails',
  NoInvoiceError = 'noInvoiceError',
  PasswordUpdateSuccess = 'passwordUpdateSuccess',
  ProfileUpdateSuccess = 'profileUpdateSuccess',
  QuoteConvertError = 'quoteConvertError',
  ListConvertToQuoteError = 'listConvertToQuoteError',
  QuickOrderConvertToQuoteError = 'quickOrderConvertToQuoteError',
  CreateQuoteError = 'createQuoteError',
  ResetPasswordError = 'resetPasswordError',
  SaveCartSuccess = 'saveCartSuccess',
  SaveCartError = 'saveCartError',
  SavedCartDeleted = 'savedCartDeleted',
  SavedCartMerged = 'savedCartMerged',
  SavedCartAddWrongType = 'savedCartAddWrongType',
  FullRegistrationValidationError = 'fullRegistrationValidationError',
  BasicRegistrationComplete = 'basicRegistrationComplete',
  FullRegistrationSaved = 'fullRegistrationSaved',
  EmailCartDoesNotExist = 'emailCartDoesNotExist',
  CartsDeleted = 'cartsDeleted',
  CartsLoaded = 'cartsLoaded',
  OciCheckoutError = 'ociCheckoutError',
  InvalidCreditCardMask = 'invalidCreditCardMask',
  OrdersApproved = 'ordersApproved',
  OrdersApprovedError = 'ordersApprovedError',
  AttributeSelectorError = 'attributeSelectorError',
  NoPDFError = 'noPDFError',
  UserNotAuthorizedOrTokenExpired = 'userNotAuthorizedOrTokenExpired',
  ShipToOrBillToPartnerNotMatched = 'shipToOrBillToPartnerNotMatched',
  GenerateQuote = 'generateQuote',
  QuoteAlreadyExistsInCart = 'quoteAlreadyExistsInCart',
  ErrorProcessingOrder = 'errorProcessingOrder',
  ErrorProcessingOrderNoContactInfo = 'errorProcessingOrderNoContactInfo',
  PaymentDEALicenseNumberEmptySnackbar = 'paymentDEALicenseNumberEmptySnackbar',
  PaymentDEALicenseNumberMaskingErrorSnackbar = 'paymentDEALicenseNumberMaskingErrorSnackbar',
  DownloadWillBeginShortly = 'downloadWillBeginShortly',
  CartMixingError = 'cartMixingError',
  SameEmproveError = 'sameEmproveError',
  B2bCheckoutError = 'b2bCheckoutError',
  B2bLogoutError = 'b2bLogoutError',
  InvalidCreditCardNumberLength = 'invalidCreditCardNumberLength',
  InvalidCreditCardExpiry = 'invalidCreditCardExpiry',
  DelegateMessageExpired = 'delegateMessageExpired',
  BasicRegistrationCompleteKorea = 'basicRegistrationCompleteKorea',
  OrderFailedCompleteRegistration = 'orderFailedCompleteRegistration',
  DigitalProductCartMixingError = 'digitalProductCartMixingError',
  SavedDocsAdded = 'savedDocsAdded',
  SavedDocDeleted = 'savedDocDeleted',
  ErrorDeletingSavedDoc = 'errorDeletingSavedDoc',
  SavedCartAddWrongTypeToPurple = 'savedCartAddWrongTypeToPurple',
  SubscribeToTrackingNotificationsSuccess = 'subscribeToTrackingNotificationsSuccess',
  UserActivityCleared = 'userActivityCleared',
  ManageOrderError = 'manageOrderError',
  ManageOrderSuccess = 'manageOrderSuccess',
}

interface SnackbarMessageFunctions {
  genericError: () => MessageAndType
  itemsAddedToCart: (itemCount: number) => MessageAndType
  resendVerificationLink: () => MessageAndType
  resendEmailLink: () => MessageAndType
  cancelCartError: () => MessageAndType
  cartContactInfoSaveError: () => MessageAndType
  cartContactPartyInfoSaveError: () => MessageAndType
  cartCsrInstructionsSaved: () => MessageAndType
  cartCsrInstructionsError: () => MessageAndType
  cartErrorRequestingQuote: (errorMessage: string) => MessageAndType
  cartErrorSubmitting: (errorMessage: string) => MessageAndType
  cartErrorUpdating: (errorMessage: string) => MessageAndType
  cartSaveBillingAddressSuccess: () => MessageAndType
  cartSaveBillingAddressError: () => MessageAndType
  cartSavePaymentInfoSuccess: () => MessageAndType
  cartSavePaymentInfoError: () => MessageAndType
  cartShippingAddressSaveError: () => MessageAndType
  cartShippingUpdateSuccess: () => MessageAndType
  cartShippingUpdateError: () => MessageAndType
  customerNotAllowedToLinkProfileBefore: (
    time: [number, number]
  ) => MessageAndType
  errorNoRecordFoundForGivenCustomerDetails: () => MessageAndType
  noInvoiceError: () => MessageAndType
  passwordUpdateSuccess: () => MessageAndType
  profileUpdateSuccess: () => MessageAndType
  quoteConvertError: (errorMessage: string) => MessageAndType
  listConvertToQuoteError: (errorMessage: string) => MessageAndType
  quickOrderConvertToQuoteError: (errorMessage: string) => MessageAndType
  resetPasswordError: (messageKey: string) => MessageAndType
  saveCartSuccess: () => MessageAndType
  saveCartError: (messageKey: string) => MessageAndType
  savedCartDeleted: () => MessageAndType
  savedCartMerged: () => MessageAndType
  savedCartAddWrongType: (productInfo: {
    productNumber: string
    companyName: string
  }) => MessageAndType
  savedDocsAdded: () => MessageAndType
  savedDocDeleted: () => MessageAndType
  errorDeletingSavedDoc: () => MessageAndType
  subscribeToTrackingNotificationsSuccess: () => MessageAndType
  fullRegistrationValidationError: () => MessageAndType
  basicRegistrationComplete: () => MessageAndType
  fullRegistrationSaved: () => MessageAndType
  emailCartDoesNotExist: () => MessageAndType
  cartsDeleted: () => MessageAndType
  cartsLoaded: () => MessageAndType
  ociCheckoutError: (errorMessage: string) => MessageAndType
  invalidCreditCardMask: () => MessageAndType
  ordersApproved: () => MessageAndType
  ordersApprovedError: () => MessageAndType
  attributeSelectorError: () => MessageAndType
  noPDFError: () => MessageAndType
  userNotAuthorizedOrTokenExpired: () => MessageAndType
  generateQuote: () => MessageAndType
  quoteAlreadyExistsInCart: () => MessageAndType
  errorProcessingOrder: (contactInfo: {
    phoneNumber: string
    email: string
  }) => MessageAndType
  errorProcessingOrderNoContactInfo: () => MessageAndType
  paymentDEALicenseNumberEmptySnackbar: (items: string) => MessageAndType
  paymentDEALicenseNumberMaskingErrorSnackbar: () => MessageAndType
  downloadWillBeginShortly: () => MessageAndType
  cartMixingError: () => MessageAndType
  sameEmproveError: () => MessageAndType
  b2bCheckoutError: (errorMessage: string) => MessageAndType
  b2bLogoutError: (errorMessage: string) => MessageAndType
  invalidCreditCardNumberLength: () => MessageAndType
  invalidCreditCardExpiry: () => MessageAndType
  delegateMessageExpired: (messageBody: string) => MessageAndType
  basicRegistrationCompleteKorea: (messageBody: string) => MessageAndType
  orderFailedCompleteRegistration: (messageBody: string) => MessageAndType
  digitalProductCartMixingError: () => MessageAndType
  savedCartAddWrongTypeToPurple: (productInfo: {
    productNumber: string
    companyNameA: string
    companyNameB: string
  }) => MessageAndType
  userActivityCleared: () => MessageAndType
  manageOrderError: (errorDetails: { phoneNumber: string }) => MessageAndType
  manageOrderSuccess: () => MessageAndType
}

// used for 'handle x' functions that need to type setSnackbar as an arg
export interface GlobalSnackbarContextValue {
  globalSnackbarState: GlobalSnackbarState
  setGlobalSnackbarState: Dispatch<SetStateAction<GlobalSnackbarState>>
  snackbarMessages: SnackbarMessageFunctions
}

const GlobalSnackbarContext = React.createContext<GlobalSnackbarContextValue>({
  globalSnackbarState: {
    open: false,
    message: '',
    variant: null,
    disableWindowBlurListener: false,
    newToastFlag: false,
  },
  setGlobalSnackbarState: () => {
    // Added comment for avoid es-lint error
  },
  snackbarMessages: {} as SnackbarMessageFunctions,
})

export const GlobalSnackbarProvider = (props) => {
  const [globalSnackbarState, setGlobalSnackbarState] =
    React.useState<GlobalSnackbarState>({
      open: false,
      message: null,
      variant: null,
    })

  const intl = useIntl()
  const { formatMessage } = intl

  /*
    This list is a set of messages that are loaded into the Snackbar.
    Currently they are unordered but there is also a good amount of nested logic and instantiation that I don't believe they can simply be ordered quickly.

    Also this might not be the most efficient method to declare them.

    Core CORE-434 was created to eventually address this.
  */
  /* eslint-disable sort-keys */
  const snackbarMessages = {
    backendError: (backendErrorMsg: string): MessageAndType => [
      backendErrorMsg,
      SnackbarType.Error,
    ],
    genericError: (): MessageAndType => [
      formatMessage(messages.GENERIC_ERROR),
      SnackbarType.Error,
    ],
    itemsAddedToCart: (itemCount: number): MessageAndType => [
      `${itemCount} ${formatMessage(messages.ITEMS_ADDED_TO_CART)}`,
      SnackbarType.Success,
    ],
    resendVerificationLink: (): MessageAndType => [
      formatMessage(messages.RESEND_EMAIL_NOTIFICATION_SNACBAR),
      SnackbarType.Success,
    ],
    resendEmailLink: (): MessageAndType => [
      formatMessage(messages.RESEND_EMAIL_NOTIFICATION_SNACKBAR),
      SnackbarType.Success,
    ],
    cancelCartError: (): MessageAndType => [
      formatMessage({
        id: 'CANCEL_CART_ERROR',
        defaultMessage: 'An unknown error occurred cancelling the cart',
      }),
      SnackbarType.Error,
    ],
    cartContactInfoSaveError: (): MessageAndType => [
      formatMessage(messages.CART_CONTACT_INFO_SAVE_ERROR),
      SnackbarType.Error,
    ],
    cartContactPartyInfoSaveError: (): MessageAndType => [
      formatMessage(messages.CART_CONTACT_PARTY_INFO_SAVE_ERROR),
      SnackbarType.Error,
    ],
    cartCsrInstructionsSaved: (): MessageAndType => [
      formatMessage(messages.CART_CSR_INSTRUCTIONS_SAVED),
      SnackbarType.Success,
    ],
    cartCsrInstructionsError: (): MessageAndType => [
      formatMessage(messages.CART_CSR_INSTRUCTIONS_ERROR),
      SnackbarType.Error,
    ],
    cartErrorRequestingQuote: (errorMessage: string): MessageAndType => [
      formatMessage(messages.CART_ERROR_REQUESTING_QUOTE, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    cartErrorSubmitting: (errorMessage: string): MessageAndType => [
      formatMessage(messages.CART_ERROR_SUBMITTING, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    cartErrorUpdating: (errorMessage: string): MessageAndType => [
      formatMessage(messages.CART_ERROR_UPDATING, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    cartSaveBillingAddressSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_BILLING_ADDRESS_SUCCESS),
      SnackbarType.Success,
    ],
    cartSaveBillingAddressError: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_BILLING_ADDRESS_ERROR),
      SnackbarType.Error,
    ],
    cartSavePaymentInfoSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_PAYMENT_INFO_SUCCESS),
      SnackbarType.Success,
    ],
    cartSavePaymentInfoError: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_PAYMENT_INFO_ERROR),
      SnackbarType.Success,
    ],
    cartShippingAddressSaveError: (): MessageAndType => [
      formatMessage(messages.CART_SHIPPING_ADDRESS_SAVE_ERROR),
      SnackbarType.Error,
    ],
    cartShippingUpdateSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SHIPPING_UPDATE_SUCCESS),
      SnackbarType.Success,
    ],
    cartShippingUpdateError: (): MessageAndType => [
      formatMessage(messages.CART_SHIPPING_UPDATE_ERROR),
      SnackbarType.Error,
    ],
    customerNotAllowedToLinkProfileBefore: (
      time: [number, number]
    ): MessageAndType => {
      const [mins, secs] = time
      return [
        formatMessage(messages.CUSTOMER_NOT_ALLOWED_TO_LINK_PROFILE_BEFORE, {
          mins,
          secs,
        }),
        SnackbarType.Error,
      ]
    },
    errorNoRecordFoundForGivenCustomerDetails: (): MessageAndType => [
      formatMessage(
        messages.ERROR_NO_RECORD_FOUND_FOR_GIVEN_COMBINATION_OF_CUSTOMER_DETAILS
      ),
      SnackbarType.Error,
    ],
    noInvoiceError: (): MessageAndType => [
      formatMessage(messages.NO_INVOICE_ERROR),
      SnackbarType.ErrorAlert,
    ],
    passwordUpdateSuccess: (): MessageAndType => [
      formatMessage(messages.PASSWORD_SUCCESSFULLY_UPDATED),
      SnackbarType.Success,
    ],
    profileUpdateSuccess: (): MessageAndType => [
      formatMessage(messages.PROFILE_UPDATE_SUCCESS),
      SnackbarType.Success,
    ],
    quoteConvertError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.QUOTE_CONVERT_ERROR, {
        errorMessage,
      }),
      SnackbarType.ErrorAlert,
    ],
    listConvertToQuoteError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.LIST_TO_QUOTE_CONVERT_ERROR, {
        errorMessage,
      }),
      SnackbarType.ErrorAlert,
    ],
    quickOrderConvertToQuoteError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.QUICK_ORDER_TO_QUOTE_CONVERT_ERROR, {
        errorMessage,
      }),
      SnackbarType.ErrorAlert,
    ],
    createQuoteError: (errorMessage: string): MessageAndType => [
      formatMessage(
        {
          id: 'CREATE_QUOTE_ERROR',
          defaultMessage: 'An error occurred while creating your quote.',
        },
        {
          errorMessage,
        }
      ),
      SnackbarType.ErrorAlert,
    ],
    resetPasswordError: (messageKey: string): MessageAndType => [
      formatMessage(messages[messageKey]),
      SnackbarType.Error,
    ],
    saveCartSuccess: (): MessageAndType => [
      formatMessage(messages.CART_SAVE_SUCCESS),
      SnackbarType.Success,
    ],
    saveCartError: (): MessageAndType => [
      formatMessage(messages.GENERIC_ERROR),
      SnackbarType.Error,
    ],
    savedCartDeleted: (): MessageAndType => [
      formatMessage(messages.SAVED_CART_DELETED),
      SnackbarType.Success,
    ],
    savedCartMerged: (): MessageAndType => [
      formatMessage(messages.SAVED_CART_MERGED),
      SnackbarType.Success,
    ],
    savedCartAddWrongType: (productInfo: {
      productNumber: string
      companyName: string
    }): MessageAndType => [
      formatMessage(messages.SAVED_CART_ADD_WRONG_TYPE, productInfo),
      SnackbarType.ErrorAlert,
    ],
    savedDocsAdded: (): MessageAndType => [
      formatMessage({
        id: 'SAVED_DOCS_ADDED',
        defaultMessage:
          'Successfully added to Your Documents in the Document Library.',
      }),
      SnackbarType.Success,
    ],
    savedDocDeleted: (): MessageAndType => [
      formatMessage({
        id: 'DOCUMENTS_DELETE_SUCCESS',
        defaultMessage: 'Successfully removed documents from your library.',
      }),
      SnackbarType.Success,
    ],
    errorDeletingSavedDoc: (): MessageAndType => [
      formatMessage({
        id: 'DOCUMENTS_ERROR_DELETING',
        defaultMessage: 'Error Deleting Documents.',
      }),
      SnackbarType.Error,
    ],
    subscribeToTrackingNotificationsSuccess: (): MessageAndType => [
      formatMessage({
        id: 'TRACKING_NOTIFICATION_SIGNUP_SUCCESS',
        defaultMessage:
          'You are signed up to receive email notifications for delivery updates.',
      }),
      SnackbarType.Success,
    ],
    fullRegistrationValidationError: (): MessageAndType => [
      formatMessage(messages.FULL_REGISTRATION_VALIDATION_FAILED),
      SnackbarType.Error,
    ],
    basicRegistrationComplete: (): MessageAndType => [
      formatMessage(messages.BASIC_REGISTRATION_COMPLETE),
      SnackbarType.SuccessAlert,
    ],
    fullRegistrationSaved: (): MessageAndType => [
      formatMessage(messages.FULL_REGISTRATION_SAVED),
      SnackbarType.Success,
    ],
    emailCartDoesNotExist: (): MessageAndType => [
      formatMessage({
        id: 'EMAIL_CART_DOES_NOT_EXIST',
        defaultMessage: 'Email cart does not exist',
      }),
      SnackbarType.Error,
    ],
    cartsDeleted: (): MessageAndType => [
      formatMessage(messages.CARTS_DELETED),
      SnackbarType.Success,
    ],
    cartsLoaded: (): MessageAndType => [
      formatMessage(messages.CARTS_LOADED),
      SnackbarType.Success,
    ],
    ociCheckoutError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.OCI_CHECKOUT_ERROR, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    invalidCreditCardMask: (): MessageAndType => [
      formatMessage(messages.INVALID_MASK_FOR_CREDIT_CARD_NAME),
      SnackbarType.Error,
    ],
    ordersApproved: (): MessageAndType => [
      formatMessage(messages.ORDERS_APPROVED),
      SnackbarType.SuccessAlert,
    ],
    ordersApprovedError: (): MessageAndType => [
      formatMessage(messages.ORDERS_APPROVED_ERROR),
      SnackbarType.ErrorAlert,
    ],
    attributeSelectorError: (): MessageAndType => [
      formatMessage(messages.ATTRIBUTE_SELECTOR_ERROR),
      SnackbarType.ErrorAlert,
    ],
    noPDFError: (): MessageAndType => [
      formatMessage(messages.NO_PDF_ERROR),
      SnackbarType.ErrorAlert,
    ],
    userNotAuthorizedOrTokenExpired: (): MessageAndType => [
      formatMessage(messages.USER_NOT_AUTHORIZED_OR_TOKEN_EXPIRED),
      SnackbarType.ErrorAlert,
    ],
    shipToOrBillToPartnerNotMatched: (): MessageAndType => [
      formatMessage(messages.SHIPTO_OR_BILLTO_PARTNER_NOT_MATCHED),
      SnackbarType.ErrorAlert,
    ],
    generateQuote: (): MessageAndType => [
      formatMessage(messages.GENERATE_QUOTE),
      SnackbarType.ErrorAlert,
    ],
    quoteAlreadyExistsInCart: (): MessageAndType => [
      formatMessage(messages.QUOTE_ALREADY_EXISTS_IN_CART),
      SnackbarType.Error,
    ],
    errorProcessingOrder: (contactInfo: {
      phoneNumber: string
      email: string
    }): MessageAndType => {
      return [
        formatMessage(messages.ERROR_PROCESSING_ORDER, contactInfo),
        SnackbarType.ErrorAlert,
      ]
    },
    errorProcessingOrderNoContactInfo: (): MessageAndType => [
      formatMessage(messages.ERROR_PROCESSING_ORDER_NO_CONTACT_INFO),
      SnackbarType.ErrorAlert,
    ],
    paymentDEALicenseNumberEmptySnackbar: (items: string): MessageAndType => [
      formatMessage(messages.PAYMENT_DEALICENSE_NUMBER_EMPTY_SNACKBAR, {
        items,
      }),
      SnackbarType.ErrorAlert,
    ],
    paymentDEALicenseNumberMaskingErrorSnackbar: (): MessageAndType => [
      formatMessage(messages.PAYMENT_DEALICENSE_NUMBER_MASKING_ERROR_SNACKBAR),
      SnackbarType.ErrorAlert,
    ],
    downloadWillBeginShortly: (): MessageAndType => [
      formatMessage(messages.DOWNLOAD_WILL_BEGIN_SHORTLY),
      SnackbarType.Success,
    ],
    cartMixingError: (): MessageAndType => [
      formatMessage(
        messages.CANNOT_CONTAIN_OTHER_PRODUCT_TYPE_WITH_EMPROVE_PRODUCTS
      ),
      SnackbarType.ErrorAlert,
    ],
    sameEmproveError: (): MessageAndType => [
      formatMessage(messages.CANNOT_CONTAIN_SAME_DOSSIER_WITH_EMPROVE_PRODUCTS),
      SnackbarType.ErrorAlert,
    ],
    b2bCheckoutError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.B2B_CHECKOUT_ERROR, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    b2bLogoutError: (errorMessage: string): MessageAndType => [
      formatMessage(messages.B2B_LOGOUT_ERROR, {
        errorMessage,
      }),
      SnackbarType.Error,
    ],
    invalidCreditCardNumberLength: (): MessageAndType => [
      formatMessage(messages.CREDIT_CARD_NUMBER_INVALID_LENGTH),
      SnackbarType.Error,
    ],
    invalidCreditCardExpiry: (): MessageAndType => [
      formatMessage(messages.CREDIT_CARD_NUMBER_INVALID_EXPIRY),
      SnackbarType.Error,
    ],
    delegateMessageExpired: (messageBody: string): MessageAndType => [
      messageBody,
      SnackbarType.WarningAlert,
    ],
    basicRegistrationCompleteKorea: (messageBody: string): MessageAndType => [
      messageBody,
      SnackbarType.SuccessAlert,
    ],
    orderFailedCompleteRegistration: (messageBody: string): MessageAndType => [
      messageBody,
      SnackbarType.WarningAlert,
    ],
    digitalProductCartMixingError: (): MessageAndType => [
      formatMessage(messages.DIGITAL_PRODUCT_CART_MIXING_ERROR),
      SnackbarType.ErrorAlert,
    ],
    savedCartAddWrongTypeToPurple: (productInfo: {
      productNumber: string
      companyNameA: string
      companyNameB: string
    }): MessageAndType => [
      formatMessage(messages.SAVED_CART_ADD_WRONG_TYPE_TO_PURPLE, productInfo),
      SnackbarType.ErrorAlert,
    ],
    userActivityCleared: (): MessageAndType => [
      formatMessage({
        id: 'USER_ACTIVITY_CLEARED',
        defaultMessage: 'User activity cleared.',
      }),
      SnackbarType.Success,
    ],
    manageOrderError: (errorDetails: {
      phoneNumber: string
    }): MessageAndType => [
      formatMessage(messages.CANCEL_ORDER_ERROR_MESSAGE, errorDetails),
      SnackbarType.Error,
    ],
    manageOrderSuccess: (): MessageAndType => [
      formatMessage({
        id: 'THIS_ORDER_HAS_BEEN_CANCELLED',
        defaultMessage: 'This order has been cancelled',
      }),
      SnackbarType.Success,
    ],
  }
  /* eslint-enable */

  const value = React.useMemo(
    () => ({ globalSnackbarState, setGlobalSnackbarState, snackbarMessages }),
    [globalSnackbarState, setGlobalSnackbarState, snackbarMessages]
  )

  return <GlobalSnackbarContext.Provider value={value} {...props} />
}

export interface SetSnackbar {
  (messageVariant: SnackbarMessages, messageData?: any): void
}

/**
 * Custom hook for interacting with global snackbar
 */

export const useGlobalSnackbar = () => {
  const { globalSnackbarState, setGlobalSnackbarState, snackbarMessages } =
    React.useContext(GlobalSnackbarContext)

  const setSnackbar = (
    // TODO: type this to SnackbarMessages per Dylan suggestions using generics:
    /*http://www.typescriptlang.org/play/?ssl=256&ssc=1&pln=256&pc=68#code/PQKhAJwfUnN+iEChwXAH3AHSxqBeMBcAFFAJSrpYB0ABuMNDrMwLSUpqT4bZQBBYEKFQs5KmmrgAKgAsApuADGAewAmygC7yAhtoDkAZ3AaAlsYAOAGz0BPRRvBKATsssA7Q9tVqr9sBWesbaTuDmnuC6ysZ6ALbKaq5argDcMubxVsnapnru4AbRStH+ADTgAGZqNjZqAO7hAEb2Zooqdq4G5mqeprrF+gBuit6+zYpj4PGa5lXmTpXNAK7aMgXKnh2KxnGubTWu4MYrzcaKAI4rY77qWmXR9laK7CpKKgDW4Fau-oquGz2So1OqNFptGKqfSeADmuxKiniFxso2MtBkMgUyneejhygAkjM1KFwA08WFnNo1O1Zv1tN0whEBqU7KEMWhmEhueBkMhgMwCVEoXoVNoVnobH4tM0QopKjELn5EtU-vEItlcvlPM55Go1J9jGlwAArFakrSdAqRWGI9VuZR-Faw+RA6rJJ4vN4fT425CWro4vqkxJ7PTw4wALiKnns-o6geqK08Yt6USO8QMAFldnF4aRPop7NG8cDwMNJTdjAB+EuxijR0KuP3IRQADxyrl8YxW6oAyp5RZ9Za5ZM9lABvVDgPsrFQqXPgAC84BMg6+I9n89zhnK04Aoq4-scV2uhyPD8fDMgAL589ud7ueXsz9fDgo5sMR8BTyAAQQ0LQNAAMT0YZknMJlTz0QCnFA8DmzCXdpwAcTGAFzBUS8PVPeFtmbLCj2SZDIAJMJkQAoDZDUABhApfFPSCkWMSinGouiuxI8AACUkTUUYQLAiCoNXdxZgE+DhMULi6JTRQbA47RsJPVcVDxBcFPo5SZPomi+m0UVtCFGo+zAxRlOXVTdP0wzjLUUzRm0vdIEUvTPAMsUAAV6PsOyHPMoiVMMNSuzcjztG8rtfM8EyzKc6dXOMVwhSbOdtDTYx-OcU8Qu0GikpShk0oyrKdNCgr6VcYrgwsnLdIq1LUxqwKyqUwLeOuXZ0rhABFFY1BE4KtPaq4q262E+oG6TnPARTlNnZp4kg8bLKGrt5rOJbtHG1rlIAVSsDQejhVbcv2w7jthVr-IAIXMOobUo9w9i3Bc9lO+jbvumxHsA57MrnN7jGusy7oeuEntzWqrK7L7wdhSG9nilzPrM7z7ESdy-MBxc6thtGHExoyYvsnG9hB0Z0aJuzobW7R-Kp24aZambFL7eRzCsKxfo0f7-Np3L2c57mIb+3N+ZZhLPo5rmbQOo6wle3GYfpmWRdheWDEUJXydZ6Xhbli6wgF-XZbhTXjcllzzWpRJXAAOQGv8wSaDRqIAGUiT5PL+BYbEUG7FCOZQcpttQ7cd7RnfqV2Pa9n21D9gOg+SaaD0Cx3ePUFJgLUZMQOSFDzFGTwaLDu2ABFFAM+7TFPAFj0zjpkgL-Pc9cIuS7L0Jw4BKua5sYGZsdoVwMwgLj1Wzw1FHtRx+R8BvL2BoW4t7Wybr1dgmX1ejfX7ddenBOk7XnXN8MX5E-uxRT43rjJrCNzRnWwLVuuKan4BNqrxm3iLgikIxgV4pFps9auS8gEt20rePkkQwiuCqKKZQA5zwflzOGRQwFkxNX6D+acMEgKSUQooaMpAmLxAJFody8xFiuEbAyG0mBwDPniJMVwFBlwAD5wCfjzIoP8OoxwvGnHhDChFjykI4UubhvCMECLduOac5CWKwTdrReipDyF6WTNoaMLC2FSJkeg+E8ihGKGnGJficEhLEM0eRShtwaEAnoc2E6WB9EAkMTw4x-DBGKMgGpOSmkX4SPAKQLxsiTF+OEQE6y7lbIkwlqE8JXDvFfl8QomJqg4nhUitoaKsVHKBUkakyJGSzHTlyvlZKlVqr9CyiU6RaS+GmP8dk8qNTGrpWaskiJPjWlZLOiNTqoQbQPxIWEhuyQykuJtH09JAzzGxJCckBaW1xqkKma4GZJwGFwnmS06JSz2nf2SBbG0mzAo7KbHM0p-SjmVNRqMMGP1Ra83FhvRpRiFkPOWfTUG30eb-WUl85pcjfknIZoTJmiTPlhIOeCzJxzBYEwxjCmoIL4V3J+Uix5sM1ZAvFnFYpWKmllMWXi1WBtzZ7zPqC8lELBYEppQrCeyR6X3NxQE8uAJI7R3BGoz2nhva+2voHYOpDpyQHSokaMABtDxrhKiKoALrTgRVErl4AtlN2zq3HU7dO5jG7rbPu1c9C1w5TiipkBp6z3niSlJZLOU2p+IA4BGhb4HyjKS75hytWXxPrSuFTq-WItde-R+fRn6nLoZMq5PjZn7Oxf611YCAE7xAY60MfCADSRYk2wg1eUxRd5+RgFgAQHk1bq1wFrZAGQTBoDiDgMQYgzaWAkGbUQZtba8C8h4DgKt2BMBYGYFARg47J3gE4PgdgAAeNgM6uATqYY2-AwgBDlEEAQSoQg90kGAJuggYh93iAHegGtG6oDAGvVyGtD6L3wC7XWx9j6YCNvHee-AlbO0sA7W2sglAZASEIKuHAU6mGGAg9AadnAOEsEbWIPg+Az0oZ3VAA9YHJCNrfTyO9PJMQ8HoCRiwVQqgyHYJ6ZQDKkWWTlTcuElRGOwjVTwAA1NRsFmqzH0ZY5UFBG4ChmLYxAEj9BqDAGQPkl43GS2yZXAxvZsIBNvhHCJvkoB0BdrwzW193I4Cfu7a2gDpmX3fsA92tdg6hCsAwGO2DTbV0cBbQupd8HHNYHXYerdGGsNYZvcejDfnz24erXegjunCODt7f+qL9ajNgZ-WZ-9VbLNhOAzwUD57oPYEg1gXLkGXMIY-TZkg6G0PiFQ7uod2HMuXvi7wQ90WIAyH3KKeQ4BE6IiVCYO+MZnCGC2dBljphjB6hWDYZwkxwArD3lSNQMh6CCffKOcctAz6MDxM4ZbanhPreUvQSoz0XiplGECDkIApPqHpCcPb2yfE+pWyOMpWCUzdNwSuX88ACHWIQkxOxSIHHUIWM43ZrjbTuN7AY6MtHeNNLlVK+A9AAAkE5yHA-SqD1wN5wBo4zNmHxpAc0YPRH+CuFd9wVygLIAA8lAYCf4ABqtPuIElkPuPsFAbxHaR5AZ7+2XgbY3kjlVM1ICiIIpi8JsOXXjlSYj7kBPtBlOJ49uVhgUL7ntvuNnNEoC6+4qzwwKqKDi-gALtbQvlKi-N8o1iajFKA-iNo9yejoeeNl9a+XCO+fgFR+j8irvtC4-x8kTMKuick4jLQdn+4sx9igOTyn1O6dQBon+bishue8+5JbsxwvvW26R5YiSNiAdhIx1QrHtDC1MMVQ2OTiyFd+4D1Xxx2PQ8TmV6r6PuxaDcXj7TpnVOGdG6zAz5nrO49c55+b-n92C9n2L4gQJGk5qOsb3Dn33DFdIB71Hx7tAM-2xovud26fM+yAN9xI33Ezd+-zwdwKK-4BVJsmKPyxLele9TTv8Ae+iAB+6Sau6S6IGeWe6etO9ssgf4NE1+BI9swE9OfYzO+4N+d+D+eei+z+x4r+fyYUhkeSBS9k3+7K8Kv+4a-+gB8AwBfCoBfC4BV+UBMBcB1+nkV+AAmlAIgcgVAKgSPhgazlgUgE-tbi-ogGLkjlUg1EVDgplGZBoJIpQTxtQX7nQRggwaTsfswTRH2NxDwfbH2LINxHtPAQSNAYngIVTiIYgGIYoIXkDPgSctUoVFVPIdLlvnLgprvuoeHoTiAX3kwZAXoQYYgcYaYeYZYUIffvPq+Kglbg4TbpIebkMseB1GNGMv1GEJcseNcspl4d7j4QAX4a4BHr3kfhAdfobqzlAIPj1HtJzrIIgShFAA0bThzpUN9tyFsmUn7jeLYRbjgeIXgSkdIcNMeGsstBcvGnkYmuDnMiofJiHL4Urv4ZHoEZUcwTUQYX2HtDdFmOzs0fbChF0X7pAL0T4v0YMQvgkQXskfAFIavhMWckbDMaQJcekoWoUX-sUTQZABofmEEToZATsVAHtJ5BXH+McacXgtWp8XwtcXEfYbQA8ZAE8W-k8gHICm8nzCGj8VQX8aUeUYfmASCdftYVADdASO7O7C0UnhToPn2FYWYWfsyTcfEUJokY4bmM4Sis8jiQjGLEjJvksc3r7msWUQEfQcCVUfwWgVSTSXSScQyRXEyYnjsRySiWieABiX8lCmiljLCt6soU3kci3pKSSZsWSXKZSRwZwVmNrggUgSgayZzlzsicMQ4cvmMc8fjJTNCkaRiqKWaXRhKfvusRUTacwXaX+A6U6YYXwZqZ6Xcetj6Y8akabOrIjAoUUj-qGfDqsRGVKRsTKVsZAX2AABIEieSeT0nJ7qnymCHJmP5emokSEZnjH4rUoazBomkUEFlqGWnSmaGykxnVm1n0kQlQkc78FunskplclL4i6+mYndlmy9msqeFinmnhlAGRmkmMHkn8ETl1kqnTnQnoEtnYGpkjHJB8k8oOxOwuxsRqBCoipXz+ziqpySrcgyoTIKoe5KrMJAVsbwAEmqHFHdFv7Bi+BypLT9DMYdDGAqqWT-l+7uDiiuBRD-G0EHnWlHk0R7TGG06OkGH2wdFJ60m04ADqo+aeypuaUAnkRuwENJ6BN0+4yBg+ZxD6CFxgcRiAFwKgxg5x4AAxglnJq29xHZkhSON45uOqagWcLcucbchcxcxqj5-cFqg8pp2+RJw5pZmhYlwJYJFFdR+4NErO1OyBe09stltRKEBII+9sUBWY1J9s0JFhbltOwE6exFdOZFUAlOsBNJfYfuWpbZOpepzCM8ngY8C425g5hlxZVpZZZJFliBLOBIZ+MRUVt5SRslup5u28kCKQXqQM+l3hKxJRRlUZR5HBzJNFNlc5NEbJfYwEe0tJ3BF5HOFcBVS5aZK5nZiAga18lVuY1VRRtVuFgJigWhMeLFfl7F4JkJl5bVHVg10lw1Req5kAkaign8KycaHxCaXxCx+yO5YZRZ+5JZDV2h7Rs51l9sI+oJt+rOvFPICJGCSJrZhV7Zoxo18A6aECHq0ufe+axYl1Ra11hZdVaVI5QJ6ukNRYpui5O1d5rgou04ZaWmla+mjWcWPIhmg6X6yWbana7a5mPaNN1m6AtmI69mTm06zmy6c6i60AxWnmT6mAPmmGfmtWAWR6N6ogJAaGvNV6h6kWRNDaMWL6stBmvN5NfNv61NatsWGWUgEA2WBWMGq6eteWjm3NiGZWhAVWIWFWNWyGWtYWjWMtiARGEANFOIeIs2SoUIf4rgsIvYtwpgaw90kEkI8upA08vgWgCw2wzgkQcgfY4ArQ7QiCk22glQTQhguoZkjwBCmQvg1IXWKwxwKg5c1GAl8dawEQRgpgvw1c+S7tQck24ABIWYtOHCed7YDIhk+dvgkoUoUIOQew5gzQ-sMgfeZgBgegJdRQaw4cPQakdQ9gl2Um-IzACglgZIecU24AsINIMdSUKgwARdPc8Q7AMmNo6IGgtAeQ0m8uXtPtRMxg86sg3CK4sg2qbYYQOopgpAtAP9BQsIPqkQVQAI4Af4XipY4A1YID4Aeiigz8fIN2pI-8lulkj9b9H9GgpgluZSxgnCpA04feTO1oFI0YsgM0feFc490Yt9vt7kD9WDPib28hcqsgKqnCcqAADGqo3mPNlNwhOOJZpmADIDRckIaGSJBJ1ixl1scIqssOXVsqYPMHFUUN7TQ3kCo8oJfMMOYEBJUB6EozEBsKo0TJPZsGYPMEA+4O5CUG7Q0PoJXUUNuFYJSMxpEAuIiCA6okQkxDMD4k+u4NcOYM9L4+kmPQZEUPkDDdIyBawgCIvcgEg-dqQPQ2SQ7t42EJUD4F1IYFQIkwkck-dtg7QGk+Xhk+AOw7k9XJbgUwkUUyU-9mUxw5UAAIyVAABMpuCTVTSTKTR59TUkXRJwvc0YhgagzQJoHQvghgAxXT9MPThTR+-TxCIIkoFwlTcz+TvT2hSzTEyqk2Ng6z1TWzMeOzZT+cQckQTghz8ztTizXjpTigVA5aIAMge0JMXYyYWsboATKwQTCIUICsE9f9ajjwM2pwXMP04QIQs2OoFzUdrjckWI44xgKgzYzjKjQd0IHQYj+gXM0wkwwcW96EBE+QhQsDlYWszgRwPwAIBOiLPA7gIQwYl2T6lZ4Iz8lQkE4jdQXWngboooC46L5zkdULLIygwLRMlQiQeIjCadPL2wcDPAM2S0oQeghYfLbQZVjCMdgLNjvgTQJQXs3LUoM25oSwZdvgXgPg8TeTXJNTXJRTaE+EmEyklQwrlzGg1zmzCzZJTrYiykTzgjLzPAIjrgYjDQEjRQMNcj3YgUijVQyjErftGjPwfw2juj0TBjpQSb7kMgMmCIZjZGljtwNjUQdjBgJgjjgrLjJwbjOgpQG+6Ro0XUWRU0ITiJDLo0fzzgo9ur0LE9LGNr3T3rtzNpLxrgGRLbvU2R8oq4YQoQOTszRzPrhF47k7oy07U0lQFTS7NzDrlRa7zbG7E0M7lQTT4ArT4AHTXrdrxz-ejbyQ67404ygzxgwzq4YzEzYoq4Mztrq29rq2RTD7E7R7z7p71Qqzjzu7I7+7Y7J1T7rbZTz4dQN7-7d74Bh7IyYHW7MLEdHrqHI4AHL2B78HoHiHUHzzwjojpgEbugUbbT7Aiq0Qc2-sMb2qcbEQCb08Kjd9ybZjWjOj5r+jCbhjPAObudyLKbRbAIJbgwZb9jlbArigzj5rxgdbiIMgJqvcT5UcL5gq8coqX5KchQo9Pz3b7bGCYTQLkTg7km0Ht7K72hWnEcz5Mcr575x8Yqxns757zTnTf7hH6Hx+j5fKenccwqnnRnwcmT87RgBHBQRHaCNpIXrnAq4XH5Sc357g27VAAos0tOtO7s0YZEZgaguw4w0QarygZopIf49snBKj3QZYXLfQ-LTj6jTHBQTXNHkb7Yhkbo2gK8FdzEAAhPZ2h45zHs57yql7HG+QZ5+cnNFwAewy0+0-58Ow56O4RSl7p25-pxF4Z0t6nK+++6M+M5Mz+-F64Ilw9sl0fTN3t2l-N4d4t1l7O4goPFBwFwl0F9Nzp-ynNx50d+93syh+N4F5N-e7t4D+5wt5l95267CyK56xD791D+ATD2Fy9xl158HE8-m+AG2JZIWPYN1ixkAA */
    messageVariant: string,
    messageData?: any,
    newToastFlag?: boolean
  ): void => {
    const [message, variant] = snackbarMessages[messageVariant](messageData)
    setGlobalSnackbarState({
      open: true,
      message,
      variant,
      newToastFlag,
    })
  }
  return { globalSnackbarState, setGlobalSnackbarState, setSnackbar }
}
